/**
 * Immutable value object representing a monetary amount in a specific currency.
 *
 * All arithmetic is performed in **integer cents** internally, eliminating
 * JavaScript floating-point errors (e.g. `0.1 + 0.2 !== 0.3`). The public
 * API accepts and returns decimal numbers/strings for convenience.
 *
 * This is the frontend counterpart of the backend `Money` record
 * (`com.shoppro.model.Money`). The two share the same rounding rules
 * (scale 2, HALF_UP equivalent) and the same `Currency` enum values.
 *
 * @example
 * ```ts
 * const price = Money.of(49.99, 'NZD');
 * const total = price.times(3);               // Money(149.97, NZD)
 * const formatted = total.format('en-NZ');     // "NZ$149.97"
 * ```
 */

import {type Currency, CURRENCY_DISPLAY_NAMES, CURRENCY_SYMBOLS, type DiscountType, DiscountType as DiscountTypeValues, VatMode,} from '@/types/pos';

// Re-export so consumers can import everything money-related from one module.
export {type Currency, CURRENCY_SYMBOLS, CURRENCY_DISPLAY_NAMES, VatMode};

// ─── Internal helpers ───────────────────────────────────────────────────────

/**
 * Round a floating-point number to the nearest integer using HALF_UP semantics
 * (round half away from zero), matching Java BigDecimal.ROUND_HALF_UP.
 *
 * `Math.round` rounds negative half-values toward zero
 * (e.g. `Math.round(-100.5) === -100`), diverging from the backend.
 * Using `Math.sign * Math.round(Math.abs(n))` corrects that:
 * `roundHalfUp(-100.5) === -101`.
 */
const roundHalfUp = (n: number): number => Math.sign(n) * Math.round(Math.abs(n));

/** Convert a decimal amount to integer cents, rounding to avoid fp dust. */
const toCents = (amount: number): number => roundHalfUp(amount * 100);

/** Convert integer cents back to a decimal amount. */
const fromCentsToDecimal = (cents: number): number => cents / 100;

// ─── Money class ────────────────────────────────────────────────────────────

export class Money {
    /** The amount in the currency's minor unit (cents). */
    readonly cents: number;
    /** The ISO 4217 currency code. */
    readonly currency: Currency;

    // ─── Private constructor ────────────────────────────────────────────

    private constructor(cents: number, currency: Currency) {
        this.cents = cents;
        this.currency = currency;
        Object.freeze(this);
    }

    // ─── Factory methods ────────────────────────────────────────────────

    /** The decimal amount (e.g. `49.99`). */
    get amount(): number {
        return fromCentsToDecimal(this.cents);
    }

    /**
     * Create a Money from a decimal amount and currency.
     *
     * @param amount   the monetary amount (e.g. `49.99`)
     * @param currency the currency code
     */
    static of(amount: number, currency: Currency): Money {
        return new Money(toCents(amount), currency);
    }

    /**
     * Create a zero-amount Money in the given currency.
     */
    static zero(currency: Currency): Money {
        return new Money(0, currency);
    }

    // ─── Accessors ──────────────────────────────────────────────────────

    /**
     * Create a Money from a cents value.
     *
     * Fractional cent inputs are rounded to the nearest integer using HALF_UP
     * (away from zero), matching the same rule applied throughout this class.
     * Prefer passing a whole-number cent value; rely on rounding only when
     * the raw value originates from an intermediate calculation.
     *
     * @param cents    the amount in the currency's minor unit (may be fractional)
     * @param currency the currency code
     *
     * @example
     * ```ts
     * Money.fromCents(4999, 'USD')   // $49.99  (exact, no rounding)
     * Money.fromCents(4999.7, 'USD') // $50.00  (4999.7 → 5000 cents, HALF_UP)
     * Money.fromCents(-4999.5, 'USD') // -$50.00 (-4999.5 → -5000 cents, away from zero)
     * ```
     */
    static fromCents(cents: number, currency: Currency): Money {
        return new Money(roundHalfUp(cents), currency);
    }

    // ─── Arithmetic ─────────────────────────────────────────────────────

    /**
     * Static, currency-agnostic percentage calculation.
     * Returns `0` if `base` is zero.
     *
     * @param part the numerator
     * @param base the denominator
     * @returns the percentage rounded to 2 decimal places
     */
    static asPercentageOf(part: number, base: number): number {
        if (base === 0) {
            return 0;
        }
        return Money.roundToScale2((part / base) * 100);
    }

    /**
     * Calculate tax as a plain number (no currency context needed).
     * For STANDARD mode: when `taxInclusive` is false the tax is ADDED on top
     * (`base × vatRate / 100`); when true the `base` is GST-inclusive and the tax is
     * EXTRACTED from within it (`base × vatRate / (100 + vatRate)`). Returns `0` otherwise.
     */
    static calculateTaxAmount(
        subtotal: number,
        vatRate: number | null | undefined,
        vatMode: typeof VatMode[keyof typeof VatMode] | null | undefined,
        taxInclusive = false,
    ): number {
        if (vatMode === VatMode.STANDARD && vatRate != null) {
            const divisor = taxInclusive ? 100 + vatRate : 100;
            return fromCentsToDecimal(roundHalfUp((toCents(subtotal) * vatRate) / divisor));
        }
        return 0;
    }

    /**
     * Round a number to 2 decimal places (half-up).
     */
    static rounded(amount: number): number {
        return Money.roundToScale2(amount);
    }

    /**
     * Calculate cart subtotal, tax, and total using integer-cent arithmetic.
     * Drop-in replacement for the old `calculateCartTotals` utility.
     */
    static calculateCartTotals(
        cart: ReadonlyArray<{ quantity: number; unitPrice: number }>,
        vatMode: typeof VatMode[keyof typeof VatMode],
        vatRate: number,
        taxInclusive = false,
    ): { subtotal: number; taxAmount: number; total: number } {
        const lineSumCents = cart.reduce(
            (sum, item) => sum + roundHalfUp(item.quantity * item.unitPrice * 100),
            0,
        );
        if (taxInclusive) {
            // Line prices already include GST: lineSum is the gross; extract the tax and derive net.
            const taxAmountCents =
                vatMode === VatMode.STANDARD ? roundHalfUp((lineSumCents * vatRate) / (100 + vatRate)) : 0;
            return {
                subtotal: fromCentsToDecimal(lineSumCents - taxAmountCents),
                taxAmount: fromCentsToDecimal(taxAmountCents),
                total: fromCentsToDecimal(lineSumCents),
            };
        }
        const taxAmountCents =
            vatMode === VatMode.STANDARD
                ? roundHalfUp((lineSumCents * vatRate) / 100)
                : 0;
        const totalCents = lineSumCents + taxAmountCents;
        return {
            subtotal: fromCentsToDecimal(lineSumCents),
            taxAmount: fromCentsToDecimal(taxAmountCents),
            total: fromCentsToDecimal(totalCents),
        };
    }

    /**
     * Calculate cart subtotal, tax, and total with an optional header-level discount.
     * Mirrors the backend `SaleService.recalculateSaleTotals` logic exactly.
     *
     * `subtotal` is the pre-discount line-item sum. Tax is applied to the
     * discounted subtotal so the discount reduces the tax base.
     */
    static calculateCartTotalsWithDiscount(
        cart: ReadonlyArray<{ quantity: number; unitPrice: number }>,
        vatMode: typeof VatMode[keyof typeof VatMode],
        vatRate: number,
        discountType: DiscountType | null,
        discountAmount: number,
        taxInclusive = false,
    ): { subtotal: number; discountApplied: number; taxAmount: number; total: number } {
        const lineSumCents = cart.reduce(
            (sum, item) => sum + roundHalfUp(item.quantity * item.unitPrice * 100),
            0,
        );
        let discountAppliedCents = 0;
        if (discountAmount > 0 && discountType != null) {
            discountAppliedCents = Math.min(
                discountType === DiscountTypeValues.PERCENTAGE
                    ? roundHalfUp((lineSumCents * discountAmount) / 100)
                    : roundHalfUp(discountAmount * 100),
                lineSumCents,
            );
        }
        const discountedCents = lineSumCents - discountAppliedCents;
        if (taxInclusive) {
            // Line prices include GST: the discounted line sum is the gross the customer pays;
            // extract the tax from within it and report the net as the subtotal.
            const taxAmountCents =
                vatMode === VatMode.STANDARD ? roundHalfUp((discountedCents * vatRate) / (100 + vatRate)) : 0;
            return {
                subtotal: fromCentsToDecimal(discountedCents - taxAmountCents),
                discountApplied: fromCentsToDecimal(discountAppliedCents),
                taxAmount: fromCentsToDecimal(taxAmountCents),
                total: fromCentsToDecimal(discountedCents),
            };
        }
        const taxAmountCents =
            vatMode === VatMode.STANDARD
                ? roundHalfUp((discountedCents * vatRate) / 100)
                : 0;
        const totalCents = discountedCents + taxAmountCents;
        return {
            subtotal: fromCentsToDecimal(lineSumCents),
            discountApplied: fromCentsToDecimal(discountAppliedCents),
            taxAmount: fromCentsToDecimal(taxAmountCents),
            total: fromCentsToDecimal(totalCents),
        };
    }

    // ─── Tax helpers ────────────────────────────────────────────────────

    /**
     * Round a number to 2 decimal places using HALF_UP rounding (away from zero).
     *
     * Uses the string-exponent trick (`Number('1.005e2')` → `100.5`) to avoid
     * the classic IEEE 754 precision loss where `1.005 * 100 = 100.4999…`.
     * The `Math.sign / Math.abs` wrapper ensures negative half-values are rounded
     * away from zero (`-1.005` → `-1.01`), matching Java BigDecimal.ROUND_HALF_UP.
     */
    private static roundToScale2(value: number): number {
        const scaled = Number(value + 'e2');
        return Number(Math.sign(scaled) * Math.round(Math.abs(scaled)) + 'e-2');
    }

    /**
     * Add another Money value. Currencies must match.
     */
    add(other: Money): Money {
        this.requireSameCurrency(other);
        return new Money(this.cents + other.cents, this.currency);
    }

    // ─── Percentage helpers ─────────────────────────────────────────────

    /**
     * Subtract another Money value. Currencies must match.
     */
    subtract(other: Money): Money {
        this.requireSameCurrency(other);
        return new Money(this.cents - other.cents, this.currency);
    }

    /**
     * Multiply by a numeric factor (integer quantity or decimal multiplier).
     * The result is rounded to the nearest cent.
     */
    times(factor: number): Money {
        return new Money(roundHalfUp(this.cents * factor), this.currency);
    }

    // ─── Comparison helpers ─────────────────────────────────────────────

    /**
     * Negate the amount (e.g. for refunds).
     */
    negate(): Money {
        return new Money(-this.cents, this.currency);
    }

    /**
     * Calculate the tax on this amount at the given percentage rate.
     *
     * @param ratePercent the tax rate as a percentage (e.g. `15` for 15%)
     * @returns the tax amount as a Money
     *
     * @example
     * ```ts
     * Money.of(1000, 'ZAR').calculateTax(15) // Money(150.00, ZAR)
     * ```
     */
    calculateTax(ratePercent: number): Money {
        const taxCents = roundHalfUp((this.cents * ratePercent) / 100);
        return new Money(taxCents, this.currency);
    }

    /**
     * Calculate the tax respecting VAT mode.
     * Returns zero if the mode is not `STANDARD` or the rate is null/undefined.
     */
    calculateTaxWithMode(
        vatRate: number | null | undefined,
        vatMode: typeof VatMode[keyof typeof VatMode] | null | undefined,
        taxInclusive = false,
    ): Money {
        if (vatMode === VatMode.STANDARD && vatRate != null) {
            const divisor = taxInclusive ? 100 + vatRate : 100;
            return new Money(roundHalfUp((this.cents * vatRate) / divisor), this.currency);
        }
        return Money.zero(this.currency);
    }

    /**
     * Calculate what percentage this amount is of a base amount.
     * Returns `0` if the base is zero. Currencies must match.
     *
     * @returns the percentage as a number with 2 decimal places (e.g. `15.00`)
     */
    percentageOf(base: Money): number {
        this.requireSameCurrency(base);
        if (base.cents === 0) {
            return 0;
        }
        return Money.roundToScale2((this.cents / base.cents) * 100);
    }

    // ─── Display / formatting ───────────────────────────────────────────

    isNegative(): boolean {
        return this.cents < 0;
    }

    isZero(): boolean {
        return this.cents === 0;
    }

    isPositive(): boolean {
        return this.cents > 0;
    }

    /**
     * Value equality — two Money instances are equal if they have the same
     * cents and currency.
     */
    equals(other: Money): boolean {
        return this.cents === other.cents && this.currency === other.currency;
    }

    /**
     * Format using `Intl.NumberFormat` for locale-aware output.
     *
     * @param locale BCP 47 locale string (e.g. `'en-NZ'`). Defaults to
     *   `'en-US'`.
     * @returns e.g. `"NZ$1,500.00"` or `"R 1 500,00"` depending on locale
     */
    format(locale: string = 'en-US'): string {
        try {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this.currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(this.amount);
        } catch {
            // Fallback if locale/currency combination is invalid
            return `${this.currency} ${this.amount.toFixed(2)}`;
        }
    }

    // ─── Static helpers (currency-agnostic) ─────────────────────────────

    /**
     * Compact format with K / M / B suffixes for dashboard widgets.
     *
     * @param locale BCP 47 locale string. Defaults to `'en-US'`.
     * @returns e.g. `"R1.5M"`, `"$250K"`, or the full formatted amount if < 1000
     */
    formatCompact(locale: string = 'en-US'): string {
        const symbol = CURRENCY_SYMBOLS[this.currency] ?? this.currency;
        const abs = Math.abs(this.amount);
        const sign = this.amount < 0 ? '-' : '';

        const formatValue = (val: number): string =>
            val.toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
            });

        if (abs >= 1_000_000_000) {
            return `${sign}${symbol}${formatValue(abs / 1_000_000_000)}B`;
        }
        if (abs >= 1_000_000) {
            return `${sign}${symbol}${formatValue(abs / 1_000_000)}M`;
        }
        if (abs >= 1_000) {
            return `${sign}${symbol}${formatValue(abs / 1_000)}K`;
        }

        // For small values, use full Intl format without fraction digits
        try {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this.currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(this.amount);
        } catch {
            return `${symbol}${Math.round(this.amount)}`;
        }
    }

    /**
     * Simple display: currency symbol immediately followed by the amount.
     * No locale formatting, no thousand separators.
     *
     * Mirrors the backend `Money.display()` method.
     *
     * @returns e.g. `"R1500.00"`, `"$49.99"`
     */
    display(): string {
        const symbol = CURRENCY_SYMBOLS[this.currency] ?? this.currency;
        return `${symbol}${this.amount.toFixed(2)}`;
    }

    // ─── Cart totals (replaces calculateCartTotals) ─────────────────────

    /**
     * String representation: `"amount CURRENCY"`.
     * Mirrors the backend `Money.toString()`.
     */
    toString(): string {
        return `${this.amount.toFixed(2)} ${this.currency}`;
    }

    // ─── Internal ───────────────────────────────────────────────────────

    /**
     * JSON serialisation. Produces `{ amount: number, currency: string }`.
     */
    toJSON(): { amount: number; currency: Currency } {
        return {amount: this.amount, currency: this.currency};
    }

    private requireSameCurrency(other: Money): void {
        if (this.currency !== other.currency) {
            throw new Error(
                `Currency mismatch: cannot combine ${this.currency} with ${other.currency}. Use explicit conversion first.`,
            );
        }
    }
}

/**
 * Format an amount using a specific record's own currency (e.g. a `Sale`), not the
 * caller's ambient/store currency — a historical record may have been created under
 * a different currency setting than what the store is configured with today.
 */
export function formatRecordMoney(amount: number, currency: Currency, locale: string): string {
    return Money.of(amount, currency).format(locale);
}

